import * as React from "react";

import { ROLE_OPTIONS } from "@/constants/roles";
import type { RoleOption } from "@/constants/roles";
import { useGetProfileQuery } from "@/queries/hooks/profileHooks";

/**
 * Shared ordering for every role picker:
 *   1. Roles the worker has marked as preferred (alphabetical),
 *   2. divider,
 *   3. remaining roles (alphabetical).
 * Users with no preferred_roles (employers, new workers) get a single flat
 * alphabetical list with no divider. The Combobox renders a separator
 * between groups when given the nested form.
 */
export function useOrderedRoleOptions():
  | Array<RoleOption>
  | Array<Array<RoleOption>> {
  const profileQuery = useGetProfileQuery();
  const preferred = profileQuery.data?.profile.preferred_roles;
  return React.useMemo(() => {
    const byLabel = (a: RoleOption, b: RoleOption) =>
      a.label.localeCompare(b.label);
    const preferredSet = new Set(preferred ?? []);
    if (preferredSet.size === 0) {
      return [...ROLE_OPTIONS].sort(byLabel);
    }
    const preferredOpts = ROLE_OPTIONS.filter((o) =>
      preferredSet.has(o.value),
    ).sort(byLabel);
    const remaining = ROLE_OPTIONS.filter(
      (o) => !preferredSet.has(o.value),
    ).sort(byLabel);
    if (preferredOpts.length === 0) return remaining;
    if (remaining.length === 0) return preferredOpts;
    return [preferredOpts, remaining];
  }, [preferred]);
}
